import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import MarketCard from "@/components/MarketCard";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/lib/i18n";
import { Search } from "lucide-react";

export default function Markets() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [markets, setMarkets] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const { t, lang } = useI18n();

    const selectedCat = searchParams.get("categoria") || "";
    const query = searchParams.get("q") || "";
    const featured = searchParams.get("featured") === "1";

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data));
    }, []);

    useEffect(() => {
        setLoading(true);
        api.get("/markets", {
            params: {
                category: selectedCat || undefined,
                q: query || undefined,
                is_featured: featured ? true : undefined,
                limit: 48,
            },
        })
            .then((r) => setMarkets(r.data))
            .finally(() => setLoading(false));
    }, [selectedCat, query, featured]);

    const setCat = (slug) => {
        const p = new URLSearchParams(searchParams);
        if (slug) p.set("categoria", slug);
        else p.delete("categoria");
        setSearchParams(p);
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="markets-page">
            <h1 className="font-sora font-extrabold text-4xl sm:text-5xl tracking-tighter text-white">
                {featured ? "Pools destacados" : t("nav.markets")}
            </h1>
            <p className="mt-2 text-p2w-gray">
                {featured ? "Mercados destacados por la plataforma." : "Explora cientos de mercados activos."}
            </p>

            <div className="mt-8 relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-p2w-gray" />
                <Input
                    data-testid="markets-search"
                    placeholder="Buscar mercados…"
                    value={query}
                    onChange={(e) => {
                        const p = new URLSearchParams(searchParams);
                        if (e.target.value) p.set("q", e.target.value);
                        else p.delete("q");
                        setSearchParams(p);
                    }}
                    className="pl-9 bg-p2w-surface border-p2w-line text-white placeholder:text-p2w-gray focus-visible:ring-p2w-green/40"
                />
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
                <button
                    onClick={() => setCat("")}
                    data-testid="filter-all"
                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                        !selectedCat
                            ? "bg-p2w-green text-p2w-green-fg border-p2w-green shadow-glow-green"
                            : "bg-p2w-surface text-white/80 border-p2w-line hover:border-p2w-green/40"
                    }`}
                >
                    Todos
                </button>
                {categories.map((c) => (
                    <button
                        key={c.id}
                        onClick={() => setCat(c.slug)}
                        data-testid={`filter-${c.slug}`}
                        className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                            selectedCat === c.slug
                                ? "bg-p2w-green text-p2w-green-fg border-p2w-green shadow-glow-green"
                                : "bg-p2w-surface text-white/80 border-p2w-line hover:border-p2w-green/40"
                        }`}
                    >
                        {lang === "en" && c.name_en ? c.name_en : c.name}
                    </button>
                ))}
            </div>

            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {loading
                    ? Array.from({ length: 6 }).map((_, i) => (
                          <div key={i} className="h-72 rounded-2xl bg-p2w-surface border border-p2w-line animate-pulse" />
                      ))
                    : markets.map((m) => <MarketCard key={m.id} market={m} />)}
            </div>
            {!loading && markets.length === 0 && (
                <div className="text-center py-16 text-p2w-gray">No hay mercados con esos filtros.</div>
            )}
        </div>
    );
}
